import type { BadgeTone } from '@fas/ui';
import {
  DAY_OF_WEEK_LABELS,
  type FacultyLoadStatus,
  type ImportBatchStatus,
  type ImportRowStatus,
  type IsoDayOfWeek,
  type ScheduledModality,
  type ScheduleStatus,
} from '@fas/types';
import { formatTime } from '@/lib/format';

export const dayName = (day: number): string => DAY_OF_WEEK_LABELS[day as IsoDayOfWeek] ?? `Day ${day}`;
export const dayShort = (day: number): string => dayName(day).slice(0, 3);

export const MODALITY_LABELS: Record<ScheduledModality, string> = {
  FACE_TO_FACE: 'Face-to-face',
  ONLINE_SYNCHRONOUS: 'Online (synchronous)',
  HYBRID: 'Hybrid',
  REMOTE_APPROVED: 'Approved remote',
  FIELD_CLINICAL: 'Field / clinical',
};

/** Modalities that are delivered online at least in part (meeting link applies). */
export const ONLINE_MODALITIES: readonly ScheduledModality[] = ['ONLINE_SYNCHRONOUS', 'HYBRID', 'REMOTE_APPROVED'];

export const scheduleStatusTone: Record<ScheduleStatus, BadgeTone> = { DRAFT: 'info', ACTIVE: 'success', INACTIVE: 'neutral', CANCELLED: 'danger' };
export const loadStatusTone: Record<FacultyLoadStatus, BadgeTone> = { DRAFT: 'info', ACTIVE: 'success', INACTIVE: 'neutral', CANCELLED: 'danger' };
export const rowStatusTone: Record<ImportRowStatus, BadgeTone> = { VALID: 'success', WARNING: 'warning', ERROR: 'danger', SKIPPED: 'neutral', COMMITTED: 'info' };
export const batchStatusTone: Record<ImportBatchStatus, BadgeTone> = {
  UPLOADED: 'info',
  VALIDATED: 'info',
  COMMITTED: 'success',
  FAILED: 'danger',
  CANCELLED: 'neutral',
};

export function timeRange(start: string, end: string): string {
  return `${formatTime(start)} – ${formatTime(end)}`;
}

export function minutesLabel(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h && m) return `${h} h ${m} min`;
  return h ? `${h} h` : `${m} min`;
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}
